import { useState, useEffect, useRef } from "react";
import styles from "../HealthChat.module.css";
import { useAuth } from '../../../hooks/useAuth';
import { isMobileVerified, storePatientSession } from '../../../utils/storage/authStorage';
import { quickRegister, sendQuickOtp, verifyOtp } from '../../../api/auth.api';

/* ─── SVG icons ─────────────────────────────────────────────────────────── */
const PersonIcon = ({ size = 52 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const UserIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const PhoneIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

/* ─── Copy ───────────────────────────────────────────────────────────────── */
const COPY = {
  en: {
    title: "Patient Details",
    subtitle: "Please provide your information to book the appointment",
    nameLabel: "Full Name",
    namePlaceholder: "Enter your full name",
    phoneLabel: "Phone Number",
    phonePlaceholder: "Enter your phone number",
    backBtn: "Back",
    continueBtn: "Continue",
    sendOtp: "Send OTP",
    verifyOtp: "Verify & Continue",
    otpLabel: "Enter OTP",
    otpSent: "OTP sent to +91 ",
    resend: "Resend OTP",
  },
  hi: {
    title: "रोगी विवरण",
    subtitle: "अपॉइंटमेंट बुक करने के लिए अपनी जानकारी प्रदान करें",
    nameLabel: "पूरा नाम",
    namePlaceholder: "अपना पूरा नाम दर्ज करें",
    phoneLabel: "फ़ोन नंबर",
    phonePlaceholder: "अपना फ़ोन नंबर दर्ज करें",
    backBtn: "वापस",
    continueBtn: "जारी रखें",
    sendOtp: "OTP भेजें",
    verifyOtp: "सत्यापित करें और जारी रखें",
    otpLabel: "OTP दर्ज करें",
    otpSent: "OTP भेजा गया +91 ",
    resend: "OTP पुनः भेजें",
  },
  te: {
    title: "రోగి వివరాలు",
    subtitle: "అపాయింట్మెంట్ బుక్ చేయడానికి మీ సమాచారం అందించండి",
    nameLabel: "పూర్తి పేరు",
    namePlaceholder: "మీ పూర్తి పేరు నమోదు చేయండి",
    phoneLabel: "ఫోన్ నంబర్",
    phonePlaceholder: "మీ ఫోన్ నంబర్ నమోదు చేయండి",
    backBtn: "వెనుకకు",
    continueBtn: "కొనసాగించు",
    sendOtp: "OTP పంపండి",
    verifyOtp: "ధృవీకరించి కొనసాగించు",
    otpLabel: "OTP నమోదు చేయండి",
    otpSent: "OTP పంపబడింది +91 ",
    resend: "OTP మళ్ళీ పంపండి",
  },
};

export default function PatientDetailsScreen({
  lang = "en",
  patientState,
  onPatientStateChange,
  onContinue,
  onBack,
}) {
  const c = COPY[lang] || COPY.en;
  const { user } = useAuth();

  // ─── ALL local state — nothing goes to parent until onContinue ───────────
  // This is the core fix: errors, otp, loading stay LOCAL so parent never
  // re-renders this component from underneath us.
  const [name, setName]           = useState(patientState?.name || '');
  const [mobile, setMobile]       = useState(patientState?.mobile || '');
  const [otp, setOtp]             = useState(['', '', '', '', '', '']);
  const [otpSent, setOtpSent]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [errors, setErrors]       = useState({});
  const isMounted                 = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Pre-fill from auth session
  useEffect(() => {
    if (!user) return;
    if (!name && user.fullName) setName(user.fullName);
    if (!mobile && user.mobile) setMobile(user.mobile);
  }, [user]); // eslint-disable-line

  const isVerified  = isMobileVerified(mobile || user?.mobile || '');
  const canProceed  = name.trim().length > 0 && mobile.trim().length > 0;

  /* ─── Helpers ────────────────────────────────────────────────────────── */
  const safeSet = (fn) => { if (isMounted.current) fn(); };

  const setError = (key, msg) =>
    setErrors(prev => ({ ...prev, [key]: msg }));

  const clearError = (key) =>
    setErrors(prev => ({ ...prev, [key]: '' }));

  /* ─── Validation ─────────────────────────────────────────────────────── */
  const validate = () => {
    let valid = true;
    const next = {};

    if (!name.trim()) {
      next.name = 'Name is required';
      valid = false;
    } else {
      next.name = '';
    }

    if (!mobile.trim()) {
      next.mobile = 'Phone is required';
      valid = false;
    } else if (!/^[6-9]/.test(mobile)) {
      next.mobile = 'Mobile number must start with 6, 7, 8, or 9.';
      valid = false;
    } else if (!/^\d{10}$/.test(mobile)) {
      next.mobile = 'Phone must be 10 digits';
      valid = false;
    } else {
      next.mobile = '';
    }

    setErrors(prev => ({ ...prev, ...next }));
    return valid;
  };

  /* ─── Send OTP ───────────────────────────────────────────────────────── */
  const handleSendOtp = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!validate()) return;

    if (isVerified) {
      // Sync back to parent then proceed
      onPatientStateChange({ name, mobile });
      onContinue({ name, mobile });
      return;
    }

    safeSet(() => setLoading(true));

    try {
      const response = await sendQuickOtp({
        fullName: name.trim(),
        mobileNumber: mobile,
      });

      if (!response?.success) {
        if (response?.success_code === 409) {
          safeSet(() => setError('mobile',
            response?.message || 'Mobile is already registered. Please login.'
          ));
          return;
        }
        throw new Error(response?.message || 'Failed to send OTP');
      }

      safeSet(() => {
        setOtpSent(true);
        clearError('otp');
        clearError('mobile');
        setOtp(['', '', '', '', '', '']);
      });

    } catch (err) {
      safeSet(() => setError('mobile',
        err?.response?.data?.message || err?.message || 'Failed to send OTP. Try again.'
      ));
    } finally {
      safeSet(() => setLoading(false));
    }
  };

  /* ─── Verify OTP ─────────────────────────────────────────────────────── */
  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const otpValue = otp.join('');

    if (otpValue.length < 6) {
      setError('otp', 'Enter all 6 digits');
      return;
    }

    safeSet(() => setLoading(true));

    try {
      // Step 1 — Verify OTP
      const verifyResponse = await verifyOtp({
        fullName: name.trim(),
        mobileNumber: mobile,
        otp: otpValue,
      });

      if (!verifyResponse?.success) {
        // ✅ Wrong OTP — show error, stay on screen, NO reload
        safeSet(() => {
          setError('otp', verifyResponse?.message || 'Invalid OTP. Please try again.');
          setLoading(false);
        });
        return;
      }

      // Step 2 — Register
      const registerResponse = await quickRegister({
        fullName: name.trim(),
        mobileNumber: mobile,
        otp: otpValue,
        orgName: `Mukta Women's Clinic`,
      });

      if (!registerResponse?.success) {
        safeSet(() => {
          setError('otp', registerResponse?.message || 'Registration failed');
          setLoading(false);
        });
        return;
      }

      // Step 3 — Store session
      storePatientSession({
        ...registerResponse.data,
        fullName: name.trim(),
        mobile,
        isVerified: true,
      });

      // Step 4 — Sync to parent & proceed
      onPatientStateChange({ name, mobile, otpVerified: true });
      onContinue({ name, mobile });

    } catch (err) {
      console.error(err);
      safeSet(() => {
        setError('otp',
          err?.response?.data?.message || err?.message || 'Invalid OTP. Please try again.'
        );
        setLoading(false);
      });
    }
    // NOTE: No finally setLoading(false) here — we handle it per-branch above
    // so we never call setState after onContinue navigates away (unmounts us)
  };

  /* ─── OTP input ──────────────────────────────────────────────────────── */
  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    setOtp(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    if (value && index < 5) {
      document.getElementById(`otp-input-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`)?.focus();
    }
  };

  /* ─── Mobile change — reset OTP flow ────────────────────────────────── */
  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setMobile(value);
    setOtpSent(false);
    setOtp(['', '', '', '', '', '']);
    if (value.length > 0 && !/^[6-9]/.test(value)) {
      setError('mobile', 'Mobile number must start with 6, 7, 8, or 9.');
    } else {
      clearError('mobile');
    }
  };

  /* ─── Primary button ─────────────────────────────────────────────────── */
  const primaryBtnLabel = isVerified
    ? c.continueBtn
    : otpSent
      ? c.verifyOtp
      : c.sendOtp;

  const handlePrimaryAction = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isVerified) {
      if (validate()) {
        onPatientStateChange({ name, mobile });
        onContinue({ name, mobile });
      }
    } else if (otpSent) {
      handleVerifyOtp(e);
    } else {
      handleSendOtp(e);
    }
  };

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    // ✅ div, NOT form — eliminates all native form-submit behaviour
    <div className={styles.bookingFormScreen}>

      <button type="button" className={styles.selectDoctorBack} onClick={onBack}>
        ← Back
      </button>

      <div className={styles.bookingIconCircle}>
        <PersonIcon size={30} />
      </div>
      <div className={styles.bookingFormTitle}>{c.title}</div>
      <div className={styles.bookingFormSub}>{c.subtitle}</div>

      {/* Full Name */}
      <div className={styles.bookingFieldGroup}>
        <label className={styles.bookingFieldLabel}>{c.nameLabel}</label>
        <div className={styles.bookingInputWrap}>
          <span className={styles.bookingInputIcon}><UserIcon /></span>
          <input
            className={styles.bookingInput}
            placeholder={c.namePlaceholder}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (e.target.value.trim()) clearError('name');
            }}
          />
        </div>
        {errors.name && <p className={styles.errorText}>{errors.name}</p>}
      </div>

      {/* Phone */}
      <div className={styles.bookingFieldGroup}>
        <label className={styles.bookingFieldLabel}>{c.phoneLabel}</label>
        <div className={styles.bookingInputWrap}>
          <span className={styles.bookingInputIcon}><PhoneIcon /></span>
          <span className={styles.bookingInputDigit} style={{ color: '#374151' }}>+91</span>
          <input
            className="px-2 py-3 text-sm outline-none bg-white"
            placeholder={c.phonePlaceholder}
            value={mobile}
            type="tel"
            maxLength={10}
            onChange={handleMobileChange}
          />
        </div>
        {errors.mobile && <p className={styles.errorText}>{errors.mobile}</p>}
      </div>

      {/* OTP Section */}
      {!isVerified && otpSent && (
        <div className={styles.bookingFieldGroup}>
          <label className={styles.bookingFieldLabel}>{c.otpLabel}</label>
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
            {c.otpSent}{mobile}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            {otp.map((digit, i) => (
              <input
                key={i}
                id={`otp-input-${i}`}
                type="tel"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(i, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(i, e)}
                style={{
                  width: 40, height: 44, textAlign: 'center',
                  fontSize: 18, border: '1px solid #d1d5db',
                  borderRadius: 8, outline: 'none', background: '#fff',
                }}
              />
            ))}
          </div>
          {errors.otp && <p className={styles.errorText}>{errors.otp}</p>}
          <button
            type="button"
            style={{
              marginTop: 8, fontSize: 13, color: 'var(--primary)',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}
            onClick={handleSendOtp}
            disabled={loading}
          >
            {c.resend}
          </button>
        </div>
      )}

      <div className={styles.bookingActions}>
        <button
          type="button"
          className={styles.bookingContinueBtn}
          disabled={!canProceed || loading}
          onClick={handlePrimaryAction}
        >
          {loading ? 'Please wait…' : primaryBtnLabel}
        </button>
      </div>

    </div>
  );
}

// import { useState, useEffect } from "react";
// import styles from "../HealthChat.module.css";
// import { useAuth } from '../../../hooks/useAuth';
// import { isMobileVerified, storePatientSession } from '../../../utils/storage/authStorage';
// import { quickRegister, sendQuickOtp, verifyOtp } from '../../../api/auth.api';
// // import StepperProgress from "../Reusablesteps/Stepperprogress";

// /* ─── SVG icons ─────────────────────────────────────────────────────────── */
// const PersonIcon = ({ size = 52 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="#fff"
//     strokeWidth="1.8"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
//     <circle cx="12" cy="7" r="4" />
//   </svg>
// );

// const UserIcon = ({ size = 16 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="#94a3b8"
//     strokeWidth="1.8"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
//     <circle cx="12" cy="7" r="4" />
//   </svg>
// );

// const PhoneIcon = ({ size = 16 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="#94a3b8"
//     strokeWidth="1.8"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
//   </svg>
// );

// /* ─── Copy ───────────────────────────────────────────────────────────────── */
// const COPY = {
//   en: {
//     title: "Patient Details",
//     subtitle: "Please provide your information to book the appointment",
//     nameLabel: "Full Name",
//     namePlaceholder: "Enter your full name",
//     phoneLabel: "Phone Number",
//     phonePlaceholder: "Enter your phone number",
//     backBtn: "Back",
//     continueBtn: "Continue",
//     sendOtp: "Send OTP",
//     verifyOtp: "Verify & Continue",
//     otpLabel: "Enter OTP",
//     otpSent: "OTP sent to +91 ",
//     resend: "Resend OTP",
//   },
//   hi: {
//     title: "रोगी विवरण",
//     subtitle: "अपॉइंटमेंट बुक करने के लिए अपनी जानकारी प्रदान करें",
//     nameLabel: "पूरा नाम",
//     namePlaceholder: "अपना पूरा नाम दर्ज करें",
//     phoneLabel: "फ़ोन नंबर",
//     phonePlaceholder: "अपना फ़ोन नंबर दर्ज करें",
//     backBtn: "वापस",
//     continueBtn: "जारी रखें",
//     sendOtp: "OTP भेजें",
//     verifyOtp: "सत्यापित करें और जारी रखें",
//     otpLabel: "OTP दर्ज करें",
//     otpSent: "OTP भेजा गया +91 ",
//     resend: "OTP पुनः भेजें",
//   },
//   te: {
//     title: "రోగి వివరాలు",
//     subtitle: "అపాయింట్మెంట్ బుక్ చేయడానికి మీ సమాచారం అందించండి",
//     nameLabel: "పూర్తి పేరు",
//     namePlaceholder: "మీ పూర్తి పేరు నమోదు చేయండి",
//     phoneLabel: "ఫోన్ నంబర్",
//     phonePlaceholder: "మీ ఫోన్ నంబర్ నమోదు చేయండి",
//     backBtn: "వెనుకకు",
//     continueBtn: "కొనసాగించు",
//     sendOtp: "OTP పంపండి",
//     verifyOtp: "ధృవీకరించి కొనసాగించు",
//     otpLabel: "OTP నమోదు చేయండి",
//     otpSent: "OTP పంపబడింది +91 ",
//     resend: "OTP మళ్ళీ పంపండి",
//   },
// };

// export default function PatientDetailsScreen({
//   lang = "en",
//   patientState,
//   onPatientStateChange,
//   onContinue,
//   onBack,
// }) {
//   const c = COPY[lang] || COPY.en;

//   const { user } = useAuth();

//   // Destructure from shared state
//   const {
//     name = '',
//     mobile = '',
//     otp = ['', '', '', '', '', ''],
//     otpSent = false,
//     otpVerified = false,
//     errors = {},
//   } = patientState;

//   const set = (d) => onPatientStateChange(d);

//   const isVerified = isMobileVerified(mobile || user?.mobile || '');

//   // Pre-fill from auth session only once (when fields are empty)
//   useEffect(() => {
//     if (!user) return;
//     if (!name && user.fullName) set({ name: user.fullName });
//     if (!mobile && user.mobile) set({ mobile: user.mobile });
//   }, [user]);

//   const [loading, setLoading] = useState(false);

//   const canProceed = name.trim() && mobile.trim();

//   /* ─── Validation ─────────────────────────────────────────────────────── */
//   // const validate = () => {
//   //   const newErrors = { ...errors };
//   //   let valid = true;

//   //   if (!name.trim()) {
//   //     newErrors.name = "Name is required";
//   //     valid = false;
//   //   } else {
//   //     newErrors.name = "";
//   //   }

//   //   if (!mobile.trim()) {
//   //     newErrors.mobile = "Phone is required";
//   //     valid = false;
//   //   } else if (!/^\d{10}$/.test(mobile)) {
//   //     newErrors.mobile = "Phone must be 10 digits";
//   //     valid = false;
//   //   } else {
//   //     newErrors.mobile = "";
//   //   }

//   //   set({ errors: newErrors });
//   //   return valid;
//   // };

//   const validate = () => {
//     const newErrors = { ...errors };
//     let valid = true;

//     if (!name.trim()) {
//       newErrors.name = "Name is required";
//       valid = false;
//     } else {
//       newErrors.name = "";
//     }

//     if (!mobile.trim()) {
//       newErrors.mobile = "Phone is required";
//       valid = false;
//     } else if (!/^[6-9]/.test(mobile)) {          // ← add this
//       newErrors.mobile = "Mobile number must start with 6, 7, 8, or 9.";
//       valid = false;
//     } else if (!/^\d{10}$/.test(mobile)) {
//       newErrors.mobile = "Phone must be 10 digits";
//       valid = false;
//     } else {
//       newErrors.mobile = "";
//     }

//     set({ errors: newErrors });
//     return valid;
//   };
//   /* ─── Send OTP ───────────────────────────────────────────────────────── */

//   const handleSendOtp = async (e) => {
//     e?.preventDefault();
//     e?.stopPropagation();

//     if (!validate()) return;

//     // Already verified
//     if (isVerified) {
//       onContinue({ name, mobile });
//       return;
//     }

//     setLoading(true);

//     try {
//       const response = await sendQuickOtp({
//         fullName: name.trim(),
//         mobileNumber: mobile,
//       });

//       if (!response?.success) {
//         // Mobile already registered
//         if (response?.success_code === 409) {
//           set({
//             errors: {
//               ...errors,
//               mobile:
//                 response?.message ||
//                 "Mobile is already registered. Please login.",
//             },
//           });

//           return;
//         }

//         throw new Error(
//           response?.message ||
//           "Failed to send OTP"
//         );
//       }

//       // =====================================
//       // SUCCESS
//       // =====================================

//       set({
//         otpSent: true,
//         errors: {
//           ...errors,
//           otp: "",
//           mobile: "",
//         },
//       });
//     } catch (err) {
//       set({
//         errors: {
//           ...errors,
//           mobile:
//             err?.response?.data?.message ||
//             err?.message ||
//             "Failed to send OTP. Try again.",
//         },
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ─── Verify OTP ─────────────────────────────────────────────────────── */
//   const handleVerifyOtp = async (e) => {
//     // ✅ PREVENT FORM SUBMIT / PAGE REFRESH
//     e?.preventDefault();
//     e?.stopPropagation();

//     if (otp.join("").length < 6) {
//       set({
//         errors: {
//           ...errors,
//           otp: "Enter all 6 digits",
//         },
//       });

//       return;
//     }

//     setLoading(true);

//     try {
//       // =====================================
//       // VERIFY OTP
//       // =====================================

//       const verifyResponse = await verifyOtp({
//         fullName: name.trim(),
//         mobileNumber: mobile,
//         otp: otp.join(""),
//       });

//       // ❌ INVALID OTP
//       if (!verifyResponse?.success) {
//         set({
//           errors: {
//             ...errors,
//             otp:
//               verifyResponse?.message ||
//               "Invalid OTP. Please try again.",
//           },
//         });

//         return;
//       }

//       // =====================================
//       // REGISTER
//       // =====================================

//       const registerResponse =
//         await quickRegister({
//           fullName: name.trim(),
//           mobileNumber: mobile,
//           otp: otp.join(""),
//           orgName: "Mukta Women's Clinic",
//         });

//       if (!registerResponse?.success) {
//         set({
//           errors: {
//             ...errors,
//             otp:
//               registerResponse?.message ||
//               "Registration failed",
//           },
//         });

//         return;
//       }

//       // =====================================
//       // STORE SESSION
//       // =====================================

//       storePatientSession({
//         ...registerResponse.data,
//         fullName: name.trim(),
//         mobile,
//         isVerified: true,
//       });

//       set({
//         otpVerified: true,
//         errors: {
//           ...errors,
//           otp: "",
//         },
//       });

//       onContinue({
//         name,
//         mobile,
//       });
//     } catch (err) {
//       console.error(err);

//       set({
//         errors: {
//           ...errors,
//           otp:
//             err?.response?.data?.message ||
//             err?.message ||
//             "Invalid OTP. Please try again.",
//         },
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ─── OTP input handler ──────────────────────────────────────────────── */
//   const handleOtpChange = (index, value) => {
//     if (!/^\d?$/.test(value)) return;
//     const newOtp = [...otp];
//     newOtp[index] = value;
//     set({ otp: newOtp });

//     // Auto-focus next
//     if (value && index < 5) {
//       const next = document.getElementById(`otp-input-${index + 1}`);
//       if (next) next.focus();
//     }
//   };

//   const handleOtpKeyDown = (index, e) => {
//     if (e.key === "Backspace" && !otp[index] && index > 0) {
//       const prev = document.getElementById(`otp-input-${index - 1}`);
//       if (prev) prev.focus();
//     }
//   };

//   /* ─── Button label logic ─────────────────────────────────────────────── */
//   const primaryBtnLabel = isVerified
//     ? c.continueBtn
//     : otpSent
//       ? c.verifyOtp
//       : c.sendOtp;

//   const handlePrimaryAction = (e) => {
//     e?.preventDefault();
//     e?.stopPropagation();
//     if (isVerified) {
//       if (validate()) onContinue({ name, mobile });
//     } else if (otpSent) {
//       handleVerifyOtp(e);
//     } else {
//       handleSendOtp(e);
//     }
//   };


// return (
//   <form className={styles.bookingFormScreen} onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }} >
//     <button type="button" className={styles.selectDoctorBack} onClick={onBack}>
//       ← Back
//     </button>

//     {/* Icon */}
//     <div className={styles.bookingIconCircle}>
//       <PersonIcon size={30} />
//     </div>
//     <div className={styles.bookingFormTitle}>{c.title}</div>
//     <div className={styles.bookingFormSub}>{c.subtitle}</div>

//     {/* Full Name */}
//     <div className={styles.bookingFieldGroup}>
//       <label className={styles.bookingFieldLabel}>{c.nameLabel}</label>
//       <div className={styles.bookingInputWrap}>
//         <span className={styles.bookingInputIcon}>
//           <UserIcon />
//         </span>
//         <input
//           className={styles.bookingInput}
//           placeholder={c.namePlaceholder}
//           value={name}
//           onChange={(e) => set({ name: e.target.value })}
//         />
//       </div>
//       {errors.name && <p className={styles.errorText}>{errors.name}</p>}
//     </div>

//     {/* Phone */}
//     <div className={styles.bookingFieldGroup}>
//       <label className={styles.bookingFieldLabel}>{c.phoneLabel}</label>
//       <div className={styles.bookingInputWrap}>
//         <span className={styles.bookingInputIcon}>
//           <PhoneIcon />
//         </span>
//         <span
//           className={`${styles.bookingInputDigit}`}
//           style={{ color: "#374151" }}
//         >
//           +91
//         </span>
//         <input
//           className="px-2 py-3 text-sm outline-none bg-white"
//           placeholder={c.phonePlaceholder}
//           value={mobile}
//           type="tel"
//           maxLength={10}
//           // onChange={(e) => {
//           //   const value = e.target.value.replace(/\D/g, "");
//           //   set({ mobile: value, otpSent: false, otpVerified: false, otp: ['', '', '', '', '', ''] });
//           // }}
//           onChange={(e) => {
//             const value = e.target.value.replace(/\D/g, "");

//             // ── Starting digit validation ──
//             let mobileError = "";
//             if (value.length > 0 && !/^[6-9]/.test(value)) {
//               mobileError = "Mobile number must start with 6, 7, 8, or 9.";
//             }

//             set({
//               mobile: value,
//               otpSent: false,
//               otpVerified: false,
//               otp: ['', '', '', '', '', ''],
//               errors: { ...errors, mobile: mobileError }
//             });
//           }}
//         />
//       </div>
//       {errors.mobile && <p className={styles.errorText}>{errors.mobile}</p>}
//     </div>

//     {/* OTP Section — shown only when not pre-verified and OTP has been sent */}
//     {!isVerified && otpSent && (
//       <div className={styles.bookingFieldGroup}>
//         <label className={styles.bookingFieldLabel}>{c.otpLabel}</label>
//         <p style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
//           {c.otpSent}{mobile}
//         </p>
//         <div style={{ display: "flex", gap: 8 }}>
//           {otp.map((digit, i) => (
//             <input
//               key={i}
//               id={`otp-input-${i}`}
//               type="tel"
//               maxLength={1}
//               value={digit}
//               onChange={(e) => handleOtpChange(i, e.target.value)}
//               onKeyDown={(e) => handleOtpKeyDown(i, e)}
//               style={{
//                 width: 40,
//                 height: 44,
//                 textAlign: "center",
//                 fontSize: 18,
//                 border: "1px solid #d1d5db",
//                 borderRadius: 8,
//                 outline: "none",
//                 background: "#fff",
//               }}
//             />
//           ))}
//         </div>
//         {errors.otp && <p className={styles.errorText}>{errors.otp}</p>}
//         <button
//           type="button"
//           style={{
//             marginTop: 8,
//             fontSize: 13,
//             color: "#7c3aed",
//             background: "none",
//             border: "none",
//             cursor: "pointer",
//             padding: 0,
//           }}
//           onClick={handleSendOtp}
//           disabled={loading}
//         >
//           {c.resend}
//         </button>
//       </div>
//     )}

//     <div className={styles.bookingActions}>
//       <button
//         type="button"
//         className={styles.bookingContinueBtn}
//         disabled={!canProceed || loading}
//         onClick={handlePrimaryAction}
//       >
//         {loading ? "Please wait…" : primaryBtnLabel}
//       </button>
//     </div>
//   </form>
// );
// }



// import { useState } from "react";
// import styles from "../HealthChat.module.css";

// /* ─── SVG icons ─────────────────────────────────────────────────────────── */
// const PersonIcon = ({ size = 52 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="#fff"
//     strokeWidth="1.8"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
//     <circle cx="12" cy="7" r="4" />
//   </svg>
// );

// const UserIcon = ({ size = 16 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="#94a3b8"
//     strokeWidth="1.8"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
//     <circle cx="12" cy="7" r="4" />
//   </svg>
// );

// const MailIcon = ({ size = 16 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="#94a3b8"
//     strokeWidth="1.8"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
//     <polyline points="22,6 12,13 2,6" />
//   </svg>
// );

// const PhoneIcon = ({ size = 16 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="#94a3b8"
//     strokeWidth="1.8"
//     strokeLinecap="round"
//     strokeLinejoin="round"
//   >
//     <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
//   </svg>
// );

// /* ─── Copy ───────────────────────────────────────────────────────────────── */
// const COPY = {
//   en: {
//     title: "Patient Details",
//     subtitle: "Please provide your information to book the appointment",
//     nameLabel: "Full Name",
//     namePlaceholder: "Enter your full name",
//     emailLabel: "Email Address",
//     emailPlaceholder: "Enter your email",
//     phoneLabel: "Phone Number",
//     phonePlaceholder: "Enter your phone number",
//     backBtn: "Back",
//     continueBtn: "Continue",
//   },
//   hi: {
//     title: "रोगी विवरण",
//     subtitle: "अपॉइंटमेंट बुक करने के लिए अपनी जानकारी प्रदान करें",
//     nameLabel: "पूरा नाम",
//     namePlaceholder: "अपना पूरा नाम दर्ज करें",
//     emailLabel: "ईमेल पता",
//     emailPlaceholder: "अपना ईमेल दर्ज करें",
//     phoneLabel: "फ़ोन नंबर",
//     phonePlaceholder: "अपना फ़ोन नंबर दर्ज करें",
//     backBtn: "वापस",
//     continueBtn: "जारी रखें",
//   },
//   te: {
//     title: "రోగి వివరాలు",
//     subtitle: "అపాయింట్మెంట్ బుక్ చేయడానికి మీ సమాచారం అందించండి",
//     nameLabel: "పూర్తి పేరు",
//     namePlaceholder: "మీ పూర్తి పేరు నమోదు చేయండి",
//     emailLabel: "ఇమెయిల్ చిరునామా",
//     emailPlaceholder: "మీ ఇమెయిల్ నమోదు చేయండి",
//     phoneLabel: "ఫోన్ నంబర్",
//     phonePlaceholder: "మీ ఫోన్ నంబర్ నమోదు చేయండి",
//     backBtn: "వెనుకకు",
//     continueBtn: "కొనసాగించు",
//   },
// };

// export default function PatientDetailsScreen({
//   lang = "en",
//   onContinue,
//   onBack,
// }) {
//   const c = COPY[lang] || COPY.en;
//   const [name, setName] = useState("");
//   const [email, setEmail] = useState("");
//   const [phone, setPhone] = useState("");
//   const [errors, setErrors] = useState({
//     name: "",
//     email: "",
//     phone: "",
//   });
//   const canContinue = name.trim() && email.trim() && phone.trim();
//   const validate = () => {
//     const newErrors = {
//       name: "",
//       email: "",
//       phone: "",
//     };

//     if (!name.trim()) {
//       newErrors.name = "Name is required";
//     }

//     if (!email.trim()) {
//       newErrors.email = "Email is required";
//     } else if (!/^\S+@\S+\.\S+$/.test(email)) {
//       newErrors.email = "Enter a valid email";
//     }

//     if (!phone.trim()) {
//       newErrors.phone = "Phone is required";
//     } else if (!/^\d{10}$/.test(phone)) {
//       newErrors.phone = "Phone must be 10 digits";
//     }

//     setErrors(newErrors);

//     return !newErrors.name && !newErrors.email && !newErrors.phone;
//   };

//   return (
//     <div className={styles.bookingFormScreen}>
//       <button className={styles.selectDoctorBack} onClick={onBack}>
//         ← Back
//       </button>
//       {/* Icon */}
//       <div className={styles.bookingIconCircle}>
//         <PersonIcon size={30} />
//       </div>
//       <div className={styles.bookingFormTitle}>{c.title}</div>
//       <div className={styles.bookingFormSub}>{c.subtitle}</div>

//       {/* Full Name */}
//       <div className={styles.bookingFieldGroup}>
//         <label className={styles.bookingFieldLabel}>{c.nameLabel}</label>
//         <div className={styles.bookingInputWrap}>
//           <span className={styles.bookingInputIcon}>
//             <UserIcon />
//           </span>
//           <input
//             className={styles.bookingInput}
//             placeholder={c.namePlaceholder}
//             value={name}
//             onChange={(e) => setName(e.target.value)}
//           />
//         </div>
//       </div>

//       {/* Email */}
//       <div className={styles.bookingFieldGroup}>
//         <label className={styles.bookingFieldLabel}>{c.emailLabel}</label>
//         <div className={styles.bookingInputWrap}>
//           <span className={styles.bookingInputIcon}>
//             <MailIcon />
//           </span>
//           <input
//             className={styles.bookingInput}
//             placeholder={c.emailPlaceholder}
//             type="email"
//             value={email}
//             onChange={(e) => setEmail(e.target.value)}
//           />
//         </div>
//         {errors.email && <p className={styles.errorText}>{errors.email}</p>}
//       </div>

//       {/* Phone */}
//       <div className={styles.bookingFieldGroup}>
//         <label className={styles.bookingFieldLabel}>{c.phoneLabel}</label>
//         <div className={styles.bookingInputWrap}>
//           <span className={styles.bookingInputIcon}>
//             <PhoneIcon />
//           </span>
//           <span
//             className={`${styles.bookingInputDigit}`}
//             style={{ color: "#374151" }}
//           >
//             +91
//           </span>
//           <input
//             // className={styles.bookingInput}
//             className="px-2 py-3 text-sm outline-none bg-white"
//             placeholder={c.phonePlaceholder}
//             // type="number"
//             value={phone}
//             type="tel"
//             maxLength={10}
//             onChange={(e) => {
//               const value = e.target.value.replace(/\D/g, "");
//               setPhone(value);
//             }}
//             // onChange={(e) => setPhone(e.target.value)}
//           />
//         </div>
//         {errors.phone && <p className={styles.errorText}>{errors.phone}</p>}
//       </div>

//       <div className={styles.bookingActions}>
//         {/* <button className={styles.bookingBackBtn} onClick={onBack}>
//           {c.backBtn}
//         </button> */}
//         <button
//           className={styles.bookingContinueBtn}
//           disabled={!canContinue}
//           // onClick={() => onContinue({ name, email, phone })}
//           onClick={() => {
//             const ok = validate();
//             if (ok) onContinue({ name, email, phone });
//           }}
//         >
//           {c.continueBtn}
//         </button>
//       </div>
//     </div>
//   );
// }
